// Self-serve sign-up (design §5, D3) and accepting an invitation. Sign-up: company + your name + work email +
// password, a Turnstile check (Q4) that earns a ticket, then Cognito SignUp with the ticket and the emailed code.
// After the code you sign in; the app then creates the company (pending approval, Q3) with you as owner.
// Invitations: SSO if the company has it for this domain, else the same password + code steps without a ticket.
import { useEffect, useRef, useState } from "react";
import { CheckCircle2, MailCheck } from "lucide-react";
import { AuthFrame } from "../App";
import { publicApi } from "../lib/api";
import { config, confirmSignUp, loadConfig, resendCode, signIn, signUp } from "../lib/auth";
import { inputCls } from "../components/bits";
import { Button, ErrorNote, Spinner, cx } from "../components/ui";

export const SIGNUP_COMPANY = "esignit-signup-company";

declare global {
  interface Window {
    turnstile?: { render(el: HTMLElement, o: Record<string, unknown>): string; reset(id?: string): void };
  }
}

/** Cloudflare Turnstile; renders nothing (and reports "no check") when no site key is configured. */
function Turnstile({ onToken }: { onToken: (t: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const siteKey = config()?.turnstileSiteKey;
  useEffect(() => {
    if (!siteKey) {
      onToken("off");
      return;
    }
    const render = () => window.turnstile && ref.current && window.turnstile.render(ref.current, { sitekey: siteKey, callback: onToken, "expired-callback": () => onToken(""), theme: "auto" });
    if (window.turnstile) return void render();
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.onload = render;
    document.head.appendChild(s);
  }, [siteKey]);
  return siteKey ? <div ref={ref} className="flex justify-center min-h-[65px]" /> : null;
}

const PASSWORD_HINT = "At least 10 characters, with a letter and a number.";
const passwordProblem = (p: string) => (p.length < 10 || !/[a-z]/i.test(p) || !/\d/.test(p) ? PASSWORD_HINT : null);

function useConfig() {
  const [ready, setReady] = useState(Boolean(config()));
  useEffect(() => void loadConfig().then(() => setReady(true)), []);
  return ready;
}

/** The emailed 6-digit code after Cognito SignUp. */
function ConfirmStep({ email, onConfirmed }: { email: string; onConfirmed: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  return (
    <>
      <MailCheck className="size-10 mx-auto text-accent" aria-hidden />
      <h1 className="text-xl font-bold font-display mt-3">Check your email</h1>
      <p className="text-sm text-muted mt-1">
        We sent a code to <span className="font-medium text-ink">{email}</span>.
      </p>
      {err && (
        <div className="mt-4 text-left">
          <ErrorNote>{err}</ErrorNote>
        </div>
      )}
      <form
        className="mt-5 space-y-3"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setErr(null);
          try {
            await confirmSignUp(email, code.trim());
            onConfirmed();
          } catch (x) {
            setErr((x as Error).message);
            setBusy(false);
          }
        }}
      >
        <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" aria-label="Code" placeholder="123456" className={cx(inputCls, "h-12 text-center text-2xl tracking-[0.4em] font-mono")} />
        <Button variant="primary" type="submit" className="w-full h-11" busy={busy} disabled={code.length !== 6}>
          Confirm email
        </Button>
      </form>
      <button
        className="text-sm text-accent-text hover:underline mt-4 disabled:opacity-50"
        disabled={sent}
        onClick={async () => {
          try {
            await resendCode(email);
            setSent(true);
          } catch (x) {
            setErr((x as Error).message);
          }
        }}
      >
        {sent ? "Sent again" : "Send the code again"}
      </button>
    </>
  );
}

export function Signup() {
  const ready = useConfig();
  const [step, setStep] = useState<"form" | "code" | "done">("form");
  const [f, setF] = useState({ companyName: "", name: "", email: "", password: "", agree: false });
  const [turnstile, setTurnstile] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!ready) return <Spinner />;
  const set = (p: Partial<typeof f>) => setF((o) => ({ ...o, ...p }));
  const submit = async () => {
    const pw = passwordProblem(f.password);
    if (pw) return setErr(pw);
    if (!f.agree) return setErr("Agree to the Terms of Service to continue");
    setBusy(true);
    setErr(null);
    try {
      const { ticket } = await publicApi<{ ticket: string }>("/signup", { email: f.email, name: f.name, companyName: f.companyName, turnstileToken: turnstile });
      await signUp({ email: f.email.trim().toLowerCase(), password: f.password, name: f.name, ticket });
      sessionStorage.setItem(SIGNUP_COMPANY, f.companyName);
      setStep("code");
    } catch (e) {
      setErr((e as Error).message);
      window.turnstile?.reset();
      setTurnstile(config()?.turnstileSiteKey ? "" : "off");
    } finally {
      setBusy(false);
    }
  };
  const landing = config()?.landingUrl ?? "https://esignit.app";
  return (
    <AuthFrame wide>
      {step === "code" ? (
        <ConfirmStep email={f.email.trim().toLowerCase()} onConfirmed={() => setStep("done")} />
      ) : step === "done" ? (
        <>
          <CheckCircle2 className="size-10 mx-auto text-good" aria-hidden />
          <h1 className="text-xl font-bold font-display mt-3">Email confirmed</h1>
          <p className="text-sm text-muted mt-1">Sign in to finish setting up {f.companyName}.</p>
          <Button variant="primary" className="w-full h-11 mt-6" onClick={() => signIn({ loginHint: f.email.trim().toLowerCase(), back: "/" })}>
            Sign in
          </Button>
        </>
      ) : (
        <>
          <h1 className="text-2xl font-bold font-display">Register your company</h1>
          <p className="text-sm text-muted mt-1">Free to start. Send documents for signature to anyone.</p>
          {err && (
            <div className="mt-5 text-left">
              <ErrorNote>{err}</ErrorNote>
            </div>
          )}
          <form
            className="mt-6 text-left space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <Labeled label="Company name">
              <input required value={f.companyName} onChange={(e) => set({ companyName: e.target.value })} className={inputCls} autoComplete="organization" />
            </Labeled>
            <Labeled label="Your name">
              <input required value={f.name} onChange={(e) => set({ name: e.target.value })} className={inputCls} autoComplete="name" />
            </Labeled>
            <Labeled label="Work email">
              <input required type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} className={inputCls} autoComplete="email" />
            </Labeled>
            <Labeled label="Password" hint={PASSWORD_HINT}>
              <input required type="password" value={f.password} onChange={(e) => set({ password: e.target.value })} className={inputCls} autoComplete="new-password" />
            </Labeled>
            <label className="flex items-start gap-2 text-sm text-ink-2">
              <input type="checkbox" checked={f.agree} onChange={(e) => set({ agree: e.target.checked })} className="mt-1" />
              <span>
                I agree to the{" "}
                <a href={`${landing}/terms`} target="_blank" rel="noopener" className="text-accent-text hover:underline">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href={`${landing}/privacy`} target="_blank" rel="noopener" className="text-accent-text hover:underline">
                  Privacy Policy
                </a>
                .
              </span>
            </label>
            <Turnstile onToken={setTurnstile} />
            <Button variant="primary" type="submit" className="w-full h-11" busy={busy} disabled={!turnstile}>
              Create account
            </Button>
          </form>
          <p className="text-xs text-muted mt-4">New companies are reviewed before they can send (usually within one business day). You can set everything up meanwhile.</p>
          <div className="mt-5 pt-4 border-t border-line text-sm">
            Already have an account?{" "}
            <a href="/" className="font-medium text-accent-text hover:underline">
              Sign in
            </a>
          </div>
        </>
      )}
    </AuthFrame>
  );
}

/** /join?email=…: accepting an invitation. */
export function Join() {
  const ready = useConfig();
  const email = (new URLSearchParams(location.search).get("email") ?? "").toLowerCase();
  const [d, setD] = useState<{ method: string; provider?: string; company?: string } | null>(null);
  const [step, setStep] = useState<"choose" | "code" | "done">("choose");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (ready && email) publicApi("/discover", { email }).then(setD, (e) => setErr((e as Error).message));
  }, [ready, email]);
  if (!ready || (!d && !err)) return <Spinner />;
  return (
    <AuthFrame>
      {step === "code" ? (
        <ConfirmStep email={email} onConfirmed={() => setStep("done")} />
      ) : step === "done" ? (
        <>
          <CheckCircle2 className="size-10 mx-auto text-good" aria-hidden />
          <h1 className="text-xl font-bold font-display mt-3">You're in</h1>
          <Button variant="primary" className="w-full h-11 mt-6" onClick={() => signIn({ loginHint: email, back: "/" })}>
            Sign in
          </Button>
        </>
      ) : (
        <>
          <h1 className="text-2xl font-bold font-display">Accept your invitation</h1>
          <p className="text-sm text-muted mt-1">{email}</p>
          {err && (
            <div className="mt-5 text-left">
              <ErrorNote>{err}</ErrorNote>
            </div>
          )}
          {d?.method === "sso" ? (
            <Button variant="primary" className="w-full h-11 mt-6" onClick={() => signIn({ provider: d.provider, loginHint: email, back: "/" })}>
              Sign in with {d.company}
            </Button>
          ) : (
            <form
              className="mt-6 text-left space-y-3"
              onSubmit={async (e) => {
                e.preventDefault();
                const pw = passwordProblem(password);
                if (pw) return setErr(pw);
                setBusy(true);
                setErr(null);
                try {
                  await signUp({ email, password, name });
                  setStep("code");
                } catch (x) {
                  const m = (x as Error).message;
                  // Already registered: just sign in.
                  if (/already has an account/i.test(m)) return void signIn({ loginHint: email, back: "/" });
                  setErr(m);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Labeled label="Your name">
                <input required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} autoComplete="name" />
              </Labeled>
              <Labeled label="Choose a password" hint={PASSWORD_HINT}>
                <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} autoComplete="new-password" />
              </Labeled>
              <Button variant="primary" type="submit" className="w-full h-11" busy={busy}>
                Create my account
              </Button>
            </form>
          )}
        </>
      )}
    </AuthFrame>
  );
}

function Labeled({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink-2 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted mt-1">{hint}</span>}
    </label>
  );
}
